import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calculator, CurrencyInr, CheckCircle, ArrowRight,
  Info, Buildings, Warning, PaperPlaneTilt, Spinner
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GST_THRESHOLD = 2000000; // ₹20L mandatory registration threshold
const GST_RATE = 0.18;         // 18% (SAC 996311 — short-term accommodation)

function fmt(n) {
  return Math.round(n).toLocaleString("en-IN");
}

function calcGST({ annual_revenue, registered }) {
  if (!registered) {
    const gap = Math.max(0, GST_THRESHOLD - annual_revenue);
    return { liable: false, gap, gst_payable: 0, net_revenue: annual_revenue };
  }
  const gst_payable = annual_revenue * GST_RATE;
  const net_revenue = annual_revenue - gst_payable;
  return { liable: true, gap: 0, gst_payable, net_revenue };
}

export default function GSTCalculator() {
  const [form, setForm] = useState({
    monthly_revenue: "",
    listing_type: "short_term",
    registered: "no",
  });
  const [result, setResult] = useState(null);
  const [leadForm, setLeadForm] = useState({ email: "", name: "" });
  const [leadSent, setLeadSent] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);

  const annual = parseFloat(form.monthly_revenue || 0) * 12;

  function handleCalculate(e) {
    e.preventDefault();
    if (!form.monthly_revenue || parseFloat(form.monthly_revenue) <= 0) return;
    const registered = form.registered === "yes";
    setResult({ annual, ...calcGST({ annual_revenue: annual, registered }) });
  }

  async function handleLeadSubmit(e) {
    e.preventDefault();
    if (!leadForm.email) return;
    setLeadLoading(true);
    try {
      await fetch(`${API}/gst-calculator/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...leadForm, annual_revenue: annual, registered: form.registered === "yes" }),
      });
    } catch (_) {}
    setLeadSent(true);
    setLeadLoading(false);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <a href="https://hostfolio.in" className="font-manrope text-lg font-bold">
            <span className="text-slate-800">Host</span><span className="text-amber-500">Folio</span>
          </a>
          <a href="https://hostfolio.in/login"
            className="text-xs font-semibold bg-amber-400 hover:bg-amber-500 text-white px-3 py-1.5 rounded-lg transition">
            Try HostFolio Free →
          </a>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-10 space-y-6">
        {/* Hero */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full">
            <Calculator size={13} weight="fill" /> Free GST Calculator for Airbnb Hosts
          </div>
          <h1 className="text-3xl font-bold text-slate-800">How much GST do you owe?</h1>
          <p className="text-slate-500 text-sm">
            India's only GST calculator built specifically for short-term rental hosts.
            Find out your liability in 30 seconds.
          </p>
        </div>

        {/* Calculator card */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 space-y-5">
          <form onSubmit={handleCalculate} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Average monthly rental income (₹)
              </label>
              <div className="relative">
                <CurrencyInr size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="number"
                  value={form.monthly_revenue}
                  onChange={e => { setForm(f => ({ ...f, monthly_revenue: e.target.value })); setResult(null); }}
                  placeholder="e.g. 80000"
                  className="w-full pl-8 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-amber-400 bg-slate-50"
                />
              </div>
              {annual > 0 && (
                <p className="text-xs text-slate-400 mt-1">Annual revenue: ₹{fmt(annual)}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Listing type</label>
              <div className="grid grid-cols-2 gap-2">
                {[["short_term", "Short-term (Airbnb/OTA)"], ["long_term", "Long-term (30+ days)"]].map(([v, label]) => (
                  <button key={v} type="button"
                    onClick={() => { setForm(f => ({ ...f, listing_type: v })); setResult(null); }}
                    className={`py-2.5 px-3 text-xs font-semibold rounded-xl border transition ${
                      form.listing_type === v
                        ? "bg-amber-400 border-amber-400 text-white"
                        : "bg-slate-50 border-slate-200 text-slate-600 hover:border-amber-300"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Are you GST registered?</label>
              <div className="grid grid-cols-2 gap-2">
                {[["no", "Not registered"], ["yes", "GST registered"]].map(([v, label]) => (
                  <button key={v} type="button"
                    onClick={() => { setForm(f => ({ ...f, registered: v })); setResult(null); }}
                    className={`py-2.5 px-3 text-xs font-semibold rounded-xl border transition ${
                      form.registered === v
                        ? "bg-amber-400 border-amber-400 text-white"
                        : "bg-slate-50 border-slate-200 text-slate-600 hover:border-amber-300"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <button type="submit"
              className="w-full py-3 bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm rounded-2xl transition flex items-center justify-center gap-2 shadow-sm">
              <Calculator size={16} weight="fill" /> Calculate My GST
            </button>
          </form>

          {/* Result */}
          <AnimatePresence>
            {result && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="border-t border-slate-100 pt-5 space-y-4">
                {!result.liable ? (
                  <div className="bg-green-50 border border-green-100 rounded-2xl p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={18} weight="fill" className="text-green-500" />
                      <p className="font-semibold text-green-800 text-sm">No GST required right now</p>
                    </div>
                    <p className="text-xs text-green-700">
                      Your annual revenue of <strong>₹{fmt(result.annual)}</strong> is below the ₹20L mandatory registration threshold.
                      You can earn <strong>₹{fmt(result.gap)}</strong> more before GST registration becomes compulsory.
                    </p>
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-start gap-2 mt-2">
                      <Info size={13} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-amber-700">
                        Voluntary registration is possible and can help with ITC (input tax credit) claims if you have business expenses.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
                      <p className="text-xs text-slate-500 mb-1">Annual GST payable (18% on ₹{fmt(result.annual)})</p>
                      <p className="text-3xl font-bold text-amber-600">₹{fmt(result.gst_payable)}</p>
                      <p className="text-xs text-slate-400 mt-1">≈ ₹{fmt(result.gst_payable / 12)} / month · ₹{fmt(result.gst_payable / 4)} / quarter</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                        <p className="text-slate-400">Gross revenue</p>
                        <p className="font-bold text-slate-700 mt-0.5">₹{fmt(result.annual)}</p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                        <p className="text-slate-400">Net (after GST)</p>
                        <p className="font-bold text-slate-700 mt-0.5">₹{fmt(result.net_revenue)}</p>
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-2">
                      <Info size={13} weight="fill" className="text-blue-500 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-blue-700">
                        GST must be filed quarterly via GSTR-1 + GSTR-3B. Late filing attracts ₹50/day penalty (₹25 CGST + ₹25 SGST).
                      </p>
                    </div>
                  </div>
                )}

                {/* Lead capture */}
                {!leadSent ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
                    <p className="text-xs font-semibold text-slate-700">
                      Get a free personalised GST guide for Airbnb hosts in your inbox
                    </p>
                    <form onSubmit={handleLeadSubmit} className="space-y-2">
                      <input type="text" placeholder="Your name" value={leadForm.name}
                        onChange={e => setLeadForm(f => ({ ...f, name: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-amber-400 bg-white" />
                      <div className="flex gap-2">
                        <input type="email" required placeholder="Email address" value={leadForm.email}
                          onChange={e => setLeadForm(f => ({ ...f, email: e.target.value }))}
                          className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-amber-400 bg-white" />
                        <button type="submit" disabled={leadLoading}
                          className="px-4 py-2 bg-amber-400 hover:bg-amber-500 text-white font-semibold text-xs rounded-xl transition disabled:opacity-50 flex items-center gap-1.5 shrink-0">
                          {leadLoading ? <Spinner size={13} className="animate-spin" /> : <PaperPlaneTilt size={13} weight="fill" />}
                          Send
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-100 rounded-2xl p-4 flex items-center gap-2">
                    <CheckCircle size={16} weight="fill" className="text-green-500" />
                    <p className="text-xs text-green-700 font-medium">
                      Guide sent! Also check out HostFolio — it files your GSTR-1 automatically.
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* What HostFolio does for you */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
          <h2 className="font-bold text-slate-800 text-base">Stop doing this manually</h2>
          <div className="space-y-3">
            {[
              ["Calculate GST on each booking", "HostFolio computes it automatically from your booking data"],
              ["Prepare GSTR-1 CSV every quarter", "One-click GSTR-1 export, ready to upload to GST portal"],
              ["Track which bookings are taxable", "Dashboard shows B2C / B2B split with turnover threshold alerts"],
            ].map(([pain, fix]) => (
              <div key={pain} className="flex items-start gap-3">
                <Warning size={14} weight="fill" className="text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-slate-700 line-through text-slate-400">{pain}</p>
                  <p className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">
                    <CheckCircle size={11} weight="fill" className="text-green-500" /> {fix}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <a href="https://hostfolio.in/login"
            className="flex items-center justify-center gap-2 w-full py-3 bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm rounded-2xl transition shadow-sm">
            Start free — no credit card needed <ArrowRight size={14} weight="bold" />
          </a>
          <p className="text-[11px] text-slate-400 text-center">1-month free trial · Built for Indian Airbnb hosts</p>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-300 pb-4">
          © {new Date().getFullYear()} HostFolio · <a href="https://hostfolio.in/privacy" className="hover:underline">Privacy</a> · <a href="https://hostfolio.in/terms" className="hover:underline">Terms</a>
        </p>
      </div>
    </div>
  );
}
